import React, { useState, useEffect, useContext } from "react";
import { getAccess } from "../common/Utils";
import { Module, Permission } from "../types/dataModel";

const AccessContext = React.createContext({});

interface Props {
  children: React.ReactNode;
}

export function useAccess(): any {
  return useContext(AccessContext);
}

export function AccessProvider({ children }: Props) {
  const [userAccess, setUserAccess] = useState<any[] | null>(null);

  useEffect(() => {
    let accessDetails = getAccess();
    if (accessDetails) {
      setUserAccess(accessDetails);
    }
  }, []);

  const hasCalendarView = (): boolean => {
    return hasAccess("Calendar Management", "VIEW");
  };

  const hasCalendarCreate = (): boolean => {
    return hasAccess("Calendar Management", "CREATE");
  };

  const hasCalendarDelete = (): boolean => {
    return hasAccess("Calendar Management", "DELETE");
  };

  const hasCalendarEdit = (): boolean => {
    return hasAccess("Calendar Management", "EDIT");
  };

  // Attendance Management
  const hasAttendView = (): boolean => {
    return hasAccess("Attendance Management", "VIEW");
  };

  const hasAttendCreate = (): boolean => {
    return hasAccess("Attendance Management", "CREATE");
  };

  const hasAttendDelete = (): boolean => {
    return hasAccess("Attendance Management", "DELETE");
  };

  const hasAttendEdit = (): boolean => {
    return hasAccess("Attendance Management", "EDIT");
  };

  // User Management

  const hasUserMgntView = (): boolean => {
    return hasAccess("User Management", "VIEW");
  };

  const hasUserMgntCreate = (): boolean => {
    return hasAccess("User Management", "CREATE");
  };

  const hasUserMgntDelete = (): boolean => {
    return hasAccess("User Management", "DELETE");
  };

  const hasUserMgntEdit = (): boolean => {
    return hasAccess("User Management", "EDIT");
  };

  // Academics Management

  const hasAcademicMgntView = (): boolean => {
    return hasAccess("Academics Management", "VIEW");
  };

  const hasAcademicMgntCreate = (): boolean => {
    return hasAccess("Academics Management", "CREATE");
  };

  const hasAcademicMgntDelete = (): boolean => {
    return hasAccess("Academics Management", "DELETE");
  };

  const hasAcademicMgntEdit = (): boolean => {
    return hasAccess("Academics Management", "EDIT");
  };

  // Class Management

  const hasClassMgntView = (): boolean => {
    return hasAccess("Class Management", "VIEW");
  };

  const hasClassMgntCreate = (): boolean => {
    return hasAccess("Class Management", "CREATE");
  };

  const hasClassMgntDelete = (): boolean => {
    return hasAccess("Class Management", "DELETE");
  };

  const hasClassMgntEdit = (): boolean => {
    return hasAccess("Class Management", "EDIT");
  };

  // Performance Management

  const hasPerformanceMgntView = (): boolean => {
    return hasAccess("Performance Management", "VIEW");
  };

  const hasPerformanceMgntCreate = (): boolean => {
    return hasAccess("Performance Management", "CREATE");
  };

  const hasPerformanceMgntDelete = (): boolean => {
    return hasAccess("Performance Management", "DELETE");
  };

  const hasPerformanceMgntEdit = (): boolean => {
    return hasAccess("Performance Management", "EDIT");
  };

  //Employee Management

  const hasOrgMgntView = (): boolean => {
    return hasAccess("Employee Management", "VIEW");
  };

  const hasOrgMgntCreate = (): boolean => {
    return hasAccess("Employee Management", "CREATE");
  };

  const hasOrgMgntDelete = (): boolean => {
    return hasAccess("Employee Management", "DELETE");
  };

  const hasOrgMgntEdit = (): boolean => {
    return hasAccess("Employee Management", "EDIT");
  };

  // Gallery Management

  const hasGalleryMgntView = (): boolean => {
    return hasAccess("Gallery Management", "VIEW");
  };
  // Settings Management

  const hasSettingsView = (): boolean => {
    return hasAccess("Settings Management", "VIEW");
  };

  // Mobile App Management

  const hasMobileAppMgntView = (): boolean => {
    return hasAccess("Mobile App Management", "VIEW");
  };

  //Dashboard Management
  const hasDashView = (): boolean => {
    return hasAccess("Dashboard Management", "VIEW");
  };

  // Bulk Upload Management
  const hasBulkUploadMgmtCreate = (): boolean => {
    return hasAccess("Bulk Upload Management", "CREATE");
  };

  const hasAccess = (moduleName: string, type: string): boolean => {
    return (
      userAccess?.some((module: Module) => {
        let hasAccess = false;
        if (module.moduleName === moduleName) {
          module?.permissions?.forEach((perm: Permission) => {
            if (perm.type === type) {
              hasAccess = perm?.selected || false;
            }
          });
        }
        return hasAccess;
      }) || false
    );
  };

  const value = {
    userAccess,
    setUserAccess,
    hasUserMgntView,
    hasUserMgntCreate,
    hasUserMgntDelete,
    hasUserMgntEdit,
    hasAcademicMgntView,
    hasAcademicMgntCreate,
    hasAcademicMgntDelete,
    hasAcademicMgntEdit,
    hasClassMgntView,
    hasClassMgntCreate,
    hasClassMgntDelete,
    hasClassMgntEdit,
    hasPerformanceMgntView,
    hasPerformanceMgntCreate,
    hasPerformanceMgntDelete,
    hasPerformanceMgntEdit,
    hasOrgMgntView,
    hasOrgMgntCreate,
    hasOrgMgntDelete,
    hasOrgMgntEdit,
    hasAttendView,
    hasAttendCreate,
    hasAttendDelete,
    hasAttendEdit,
    hasCalendarView,
    hasCalendarCreate,
    hasCalendarDelete,
    hasCalendarEdit,
    hasGalleryMgntView,
    hasSettingsView,
    hasMobileAppMgntView,
    hasDashView,
    hasBulkUploadMgmtCreate,
  };

  return (
    <AccessContext.Provider value={value}>{children}</AccessContext.Provider>
  );
}
